import { useCallback, useEffect, useMemo, useState } from 'react'
import embeddingService, {
  BoundaryView, ClusterSummary, FlatCluster, FlatClusterList,
  ImageSearchItem, ScatterPoint, SplitCandidateView,
} from '../services/embeddingService'
import { Moon, RotateCcw, Sun } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import ClusterScatter3D, { Backdrop, CameraMode, colorOf } from './ClusterScatter3D'
import ClusterDownloadModal from './ClusterDownloadModal'
import ImageResultCard from './ImageResultCard'

/** 확대창에 필요한 최소 정보. 대표·경계 썸네일은 파일명이 없다. */
export type PreviewImage = { id: number; filename?: string }

type LeftView = 'scatter' | 'list'
type DetailTab = 'images' | 'stats' | 'representatives' | 'boundary' | 'split'

const PAGE = 60
/** 우측 상세의 이미지 격자 — 한 줄에 최대 5장으로 모든 탭을 통일한다.
 *  우측 칸이 화면 절반이라 8장씩 넣으면 한 장이 너무 작아 아무것도 안 보인다. */
const THUMB_GRID = 'grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5'
const count = (value: number) => value.toLocaleString()

/** 경계 비율을 「나눠볼 것」으로 강조하는 문턱.
 *
 * 경계 비율은 중심거리가 평균+1σ 를 넘는 사진의 비율이라, 거리 분포가 정규분포에
 * 가까우면 클러스터가 잘 나뉘었든 아니든 15.9% 로 수렴한다. 즉 이 값은 「덜
 * 쪼개졌다」가 아니라 「분포 꼬리가 두껍다」를 잰다 — 크기와의 상관은 0.26 으로
 * 없는 수준이고 응집도와 0.53 이다.
 *
 * 그래서 문턱은 자연값 15.9% 보다 확실히 위여야 한다. 12% 로 두었을 때 run 5
 * (14,870장·리프 49개) 에서 34개(69%)가 강조돼 경고가 배경색이 됐다. 18% 면
 * 19.2% 짜리 하나만 남는다. 데이터가 바뀌어 강조가 다시 늘어나면 값이 아니라
 * 지표를 의심할 것 — 이 판정에 실제로 반응하는 건 유사도다.
 */
const BOUNDARY_ALERT = 0.18
const percent = (value: number) => `${(Math.max(0, Math.min(1, value)) * 100).toFixed(1)}%`

/** 몇 개 중 하나를 고르는 토글.
 *
 * 낱개 버튼을 나열하면 무엇이 한 벌의 선택지인지 보이지 않아 화면이 흐트러진다.
 * 테두리를 공유하는 한 덩어리로 묶고, 고른 칸만 채운다.
 */
function Segmented<T extends string>({ value, options, onChange, className = '' }: {
  value: T
  options: { key: T; label: string; icon?: LucideIcon }[]
  onChange: (next: T) => void
  className?: string
}) {
  return (
    <div className={`inline-flex overflow-hidden rounded-md border border-gray-300 bg-white ${className}`}>
      {options.map(({ key, label, icon: Icon }, i) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={`flex items-center gap-1 px-2.5 py-1 text-xs transition ${
            i > 0 ? 'border-l border-gray-300' : ''
          } ${value === key ? 'bg-gray-800 font-medium text-white' : 'text-gray-600 hover:bg-gray-50'}`}
        >
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
        </button>
      ))}
    </div>
  )
}

/** 데이터 분포 — 좌측에서 클러스터를 고르고 우측에서 그 클러스터를 본다.
 *
 * 예전에는 「분포」 탭과 「3D 산점도」 탭이 따로 있었는데, 둘 다 "클러스터 목록 +
 * 이미지 그리드" 라 사실상 같은 화면이었고 정보만 갈라져 있었다(한쪽엔 경계 표본·
 * 분할 후보, 다른 쪽엔 통계·대표 이미지). 탭을 옮기면 선택도 초기화됐다.
 * 하나로 합치고, 좌측만 3D ↔ 목록으로 바꾼다.
 *
 * 클러스터 목록은 `clusters/flat` 하나만 읽는다. 예전에는 `clusters` 도 같이 읽었는데
 * 같은 목록을 두 곳에서 받으면 id 가 어긋날 여지가 생긴다. 유사도는 중심거리에서
 * 계산한다 — 백엔드의 avg_similarity 도 1 - 평균거리다.
 */
export default function ClusterDistributionPanel({
  runId, isAdmin, onOpenPreview,
}: {
  /** 어떤 클러스터링의 결과를 볼지. 프로젝트 화면이 정해서 넘긴다. */
  runId: number
  isAdmin: boolean
  onOpenPreview: (item: PreviewImage) => void
}) {
  const [data, setData] = useState<FlatClusterList | null>(null)
  const [points, setPoints] = useState<ScatterPoint[]>([])
  const [scatterTotal, setScatterTotal] = useState(0)
  const [selected, setSelected] = useState<FlatCluster | null>(null)

  const [items, setItems] = useState<ImageSearchItem[]>([])
  const [total, setTotal] = useState(0)
  const [boundary, setBoundary] = useState<BoundaryView | null>(null)
  const [split, setSplit] = useState<SplitCandidateView | null>(null)

  const [loading, setLoading] = useState(false)
  const [loadingItems, setLoadingItems] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')

  const [leftView, setLeftView] = useState<LeftView>('scatter')
  const [detailTab, setDetailTab] = useState<DetailTab>('images')
  const [pointSize, setPointSize] = useState(3)
  const [showLabels, setShowLabels] = useState(true)
  const [mode, setMode] = useState<CameraMode>('rotate')
  const [backdrop, setBackdrop] = useState<Backdrop>('dark')
  const [resetSignal, setResetSignal] = useState(0)

  const [editing, setEditing] = useState(false)
  const [labelDraft, setLabelDraft] = useState('')
  const [savingLabel, setSavingLabel] = useState(false)

  const [downloadOpen, setDownloadOpen] = useState(false)
  const [packaging, setPackaging] = useState<
    { total: number; progress: number | null; message: string } | null
  >(null)

  const nameOf = (c: FlatCluster) => c.label || `클러스터 ${c.node_path}`

  const selectCluster = useCallback(async (cluster: FlatCluster) => {
    setSelected(cluster)
    setDetailTab('images')
    setBoundary(null); setSplit(null); setEditing(false)
    setLoadingItems(true)
    try {
      const page = await embeddingService.clusterFiles(cluster.id, 0, PAGE, runId)
      setItems(page.items)
      setTotal(page.total)
    } catch (e: any) {
      setItems([]); setTotal(0)
      setError(e?.message || '클러스터 이미지 조회 실패')
    } finally { setLoadingItems(false) }
  }, [runId])

  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true); setError('')
      try {
        const [list, scatter] = await Promise.all([
          embeddingService.clusterFlat(runId, 'leaf'),
          embeddingService.clusterScatter(8000, runId),
        ])
        if (!alive) return
        setData(list)
        setPoints(scatter.points)
        setScatterTotal(scatter.total)
        if (list.clusters.length > 0) await selectCluster(list.clusters[0])
        else setSelected(null)
      } catch (e: any) {
        if (alive) setError(e?.message || '분포 조회 실패')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId])

  const loadMore = async () => {
    if (!selected || loadingMore || items.length >= total) return
    setLoadingMore(true)
    try {
      const page = await embeddingService.clusterFiles(selected.id, items.length, PAGE, runId)
      setItems(current => [...current, ...page.items])
      setTotal(page.total)
    } catch (e: any) { setError(e?.message || '추가 조회 실패') }
    finally { setLoadingMore(false) }
  }

  const showBoundary = async () => {
    if (!selected) return
    setDetailTab('boundary')
    if (boundary) return
    try { setBoundary(await embeddingService.clusterBoundary(selected.id, runId, 24)) }
    catch (e: any) { setError(e?.message || '경계 표본 조회 실패') }
  }

  const showSplit = async () => {
    if (!selected) return
    setDetailTab('split')
    if (split) return
    try { setSplit(await embeddingService.clusterSplitCandidates(selected.id, runId)) }
    catch (e: any) { setError(e?.message || '분할 후보 조회 실패') }
  }

  const saveLabel = async () => {
    if (!selected || savingLabel) return
    const value = labelDraft.trim()
    if (!value) return
    setSavingLabel(true)
    try {
      await embeddingService.updateClusterLabel(selected.id, value)
      setSelected({ ...selected, label: value })
      setData(current => current && {
        ...current,
        clusters: current.clusters.map(c => c.id === selected.id ? { ...c, label: value } : c),
      })
      setEditing(false)
    } catch (e: any) { setError(e?.message || '이름 저장 실패') }
    finally { setSavingLabel(false) }
  }

  const startDownload = async (name: string) => {
    if (!selected) return
    const result = await embeddingService.downloadCluster(selected.id, runId, name)
    if (result.status === 'ok') {
      setDownloadOpen(false)
      return
    }
    // 서버가 디스크에 ZIP 을 만든다. 창을 닫고 진행률을 보여주다가 준비되면 자동으로 받는다.
    setDownloadOpen(false)
    const total = result.fileCount ?? selected.visible_size
    setPackaging({ total, progress: null, message: '패키징을 준비하고 있습니다.' })
    const pollingUrl = result.pollingUrl
    const snapshot = result.snapshot
    const timer = window.setInterval(async () => {
      try {
        if (pollingUrl) {
          const st = await embeddingService.checkDownloadStatus(pollingUrl)
          if (st.status !== 'ready' && st.status !== 'completed') {
            setPackaging({
              total,
              progress: typeof st.progress === 'number' ? st.progress : null,
              message: st.message || '패키징이 진행 중입니다.',
            })
            return
          }
        }
        const again = await embeddingService.downloadCluster(selected.id, runId, name, snapshot)
        if (again.status === 'ok') {
          window.clearInterval(timer)
          setPackaging(null)
        }
      } catch (e: any) {
        window.clearInterval(timer)
        setPackaging(null)
        setError(e?.message || '다운로드 실패')
      }
    }, 2000)
  }

  const clusters = data?.clusters ?? []
  const maxSize = useMemo(() => Math.max(1, ...clusters.map(c => c.visible_size)), [clusters])

  /** 3D 컴포넌트가 쓰는 모양으로 맞춘다 (id / cluster_idx / label / size) */
  const scatterClusters: ClusterSummary[] = useMemo(() => clusters.map(c => ({
    id: c.id,
    cluster_idx: c.cluster_idx,
    label: c.label || `클러스터 ${c.node_path}`,
    size: c.visible_size,
    share: c.share,
    avg_similarity: c.mean_distance != null ? 1 - c.mean_distance : 0,
    representative_file_ids: [],
    representative_thumbnail_urls: c.representative_thumbnail_urls,
  })), [clusters])

  /** 통계 탭: 지금 불러온 항목 기준 분포 (전체가 아님을 화면에 명시한다) */
  const stats = useMemo(() => {
    const bucket = (key: (i: ImageSearchItem) => string) => {
      const m = new Map<string, number>()
      items.forEach(i => { const k = key(i) || '미상'; m.set(k, (m.get(k) || 0) + 1) })
      return Array.from(m.entries()).sort((a, b) => b[1] - a[1])
    }
    const hour = (i: ImageSearchItem) => {
      const ts = i.metadata?.common_data?.time_stamp
      if (!ts) return '미상'
      const d = new Date(ts)
      return Number.isNaN(d.getTime()) ? '미상' : `${String(d.getHours()).padStart(2, '0')}시`
    }
    return {
      subType: bucket(i => i.sub_type || ''),
      uploadMethod: bucket(i => (i.upload_method === 'auto' ? '자동수집' : '수동 업로드')),
      hour: bucket(hour),
    }
  }, [items])

  if (loading && clusters.length === 0) {
    return <div className="flex h-full items-center justify-center p-8 text-sm text-gray-500">분포 조회 중...</div>
  }
  if (clusters.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-gray-500">
        {error || '표시할 클러스터가 없습니다.'}
      </div>
    )
  }

  const similarity = selected?.mean_distance != null ? 1 - selected.mean_distance : null

  return (
    <div className="flex h-full min-h-0">
      {/* ── 좌측: 3D 분포 ↔ 목록 ── */}
      <div className="flex w-[46%] flex-none flex-col border-r border-gray-200 bg-gray-50">
        <div className="px-3 py-2 border-b border-gray-200 bg-white">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              value={leftView}
              onChange={setLeftView}
              options={[{ key: 'scatter', label: '3D 분포' }, { key: 'list', label: '목록' }]}
            />
            {leftView === 'scatter' && (
              <span className="ml-auto flex items-center gap-1.5">
                <Segmented
                  value={mode}
                  onChange={setMode}
                  options={[{ key: 'rotate', label: '회전' }, { key: 'pan', label: '이동' }]}
                />
                <button
                  type="button"
                  onClick={() => setResetSignal(v => v + 1)}
                  title="시점 초기화"
                  className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-600 transition hover:bg-gray-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  초기화
                </button>
              </span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-gray-400">
            {leftView === 'scatter'
              ? `점 색상 = 클러스터 · ${count(points.length)}${scatterTotal > points.length ? ` / ${count(scatterTotal)} (균등 표본)` : ''}점`
              : `클러스터 ${count(clusters.length)}개 · 총 ${count(data?.total_visible ?? 0)}장 · 크기순`}
          </div>
        </div>

        {leftView === 'scatter' ? (
          <>
            <div className="flex-1 min-h-0 p-2">
              {points.length === 0 ? (
                <div className="flex h-full items-center justify-center text-xs text-gray-400">
                  표시할 좌표가 없습니다.
                </div>
              ) : (
                <ClusterScatter3D
                  points={points}
                  clusters={scatterClusters}
                  selectedClusterId={selected?.id ?? null}
                  onSelectCluster={id => {
                    const target = clusters.find(c => c.id === id)
                    if (target) void selectCluster(target)
                  }}
                  pointSize={pointSize}
                  showLabels={showLabels}
                  mode={mode}
                  backdrop={backdrop}
                  resetSignal={resetSignal}
                />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 border-t border-gray-200 bg-white px-3 py-2">
              <label className="flex items-center gap-2 text-xs text-gray-600">
                점 크기
                <input
                  type="range" min={1} max={7} step={1}
                  value={pointSize}
                  onChange={e => setPointSize(Number(e.target.value))}
                  className="h-1 w-28"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={showLabels}
                  onChange={e => setShowLabels(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                클러스터 이름 표시
              </label>
              <Segmented
                className="ml-auto"
                value={backdrop}
                onChange={setBackdrop}
                options={[
                  { key: 'dark', label: '어둡게', icon: Moon },
                  { key: 'light', label: '밝게', icon: Sun },
                ]}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto p-2">
            {clusters.map(cluster => {
              const active = selected?.id === cluster.id
              const sim = cluster.mean_distance != null ? 1 - cluster.mean_distance : null
              return (
                <div
                  key={cluster.id}
                  onClick={() => void selectCluster(cluster)}
                  className={`mb-1.5 flex items-center gap-3 rounded px-2.5 py-2.5 cursor-pointer transition-colors ${
                    active ? 'bg-gray-100 border-l-4 border-gray-800' : 'hover:bg-gray-100'
                  }`}
                >
                  {/* 글자 정보 — 이름, 규모, 응집/검수 지표.
                      flex-1 로 두면 글자가 짧아도 폭을 다 차지해 가운데가 비어 보인다.
                      폭을 정해주고 남는 자리는 썸네일이 쓴다. */}
                  <div className="min-w-0 shrink-0 basis-[48%]">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: colorOf(cluster.cluster_idx) }}
                      />
                      <span className="min-w-0 truncate text-[13px] font-medium text-gray-800">
                        {nameOf(cluster)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 pl-[18px]">
                      <span className="h-2 w-24 shrink-0 overflow-hidden rounded bg-gray-200">
                        <span
                          className="block h-full rounded"
                          style={{
                            width: `${(cluster.visible_size / maxSize) * 100}%`,
                            background: colorOf(cluster.cluster_idx),
                          }}
                        />
                      </span>
                      <span className="text-xs text-gray-600">
                        {count(cluster.visible_size)}장 · {(cluster.share * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 pl-[18px] text-[11px] text-gray-400">
                      {sim != null && <span>유사도 {(sim * 100).toFixed(0)}%</span>}
                      {cluster.outlier_count > 0 && <span>이상치 {count(cluster.outlier_count)}장</span>}
                      {cluster.boundary_ratio != null && (
                        <span className={cluster.boundary_ratio >= BOUNDARY_ALERT ? 'font-medium text-amber-700' : ''}>
                          경계 {(cluster.boundary_ratio * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 대표 썸네일 — 남는 폭을 3장이 나눠 쓴다. 높이는 4:3 비율로 따라가므로
                      아래로 늘려 납작해지는 일이 없고, 빈 공간도 남지 않는다. */}
                  <div className="flex min-w-0 flex-1 gap-1">
                    {cluster.representative_thumbnail_urls.slice(0, 3).map((url, i) => (
                      <img
                        key={`${url}-${i}`}
                        src={embeddingService.mediaUrl(url)}
                        alt=""
                        className="aspect-[4/3] min-w-0 flex-1 rounded object-cover"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>
              )
            })}
            {data?.leaf_stability_note && (
              <p className="px-2 py-2 text-[11px] text-gray-400">{data.leaf_stability_note}</p>
            )}
          </div>
        )}
      </div>

      {/* ── 우측: 선택한 클러스터의 이미지와 정보 ── */}
      <div className="flex min-w-0 min-h-0 flex-1 flex-col">
        {error && (
          <div className="m-3 border border-red-200 bg-red-50 rounded px-3 py-2 text-sm text-red-600">
            {error}
          </div>
        )}

        {!selected ? (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
            좌측에서 클러스터를 선택하세요.
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ background: colorOf(selected.cluster_idx) }}
                />
                {editing ? (
                  <span className="flex items-center gap-1">
                    <input
                      value={labelDraft}
                      onChange={e => setLabelDraft(e.target.value)}
                      maxLength={200}
                      autoFocus
                      placeholder="클러스터 이름"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <button
                      onClick={saveLabel}
                      disabled={!labelDraft.trim() || savingLabel}
                      className={`px-2 py-1 text-xs rounded text-white ${
                        labelDraft.trim() && !savingLabel ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'
                      }`}
                    >{savingLabel ? '저장 중...' : '저장'}</button>
                    <button
                      onClick={() => setEditing(false)}
                      className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50"
                    >취소</button>
                  </span>
                ) : (
                  <>
                    <h4 className="truncate text-base font-semibold text-gray-900">{nameOf(selected)}</h4>
                    {isAdmin && (
                      <button
                        onClick={() => { setLabelDraft(selected.label || ''); setEditing(true) }}
                        className="px-2 py-0.5 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50"
                      >수정</button>
                    )}
                    <button
                      onClick={() => setDownloadOpen(true)}
                      className="ml-auto px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
                    >다운로드</button>
                  </>
                )}
              </div>
              {packaging && (
                <div className="mt-2 border border-blue-200 bg-blue-50 rounded px-3 py-2 text-xs text-blue-700">
                  <div className="flex items-center gap-2">
                    <span className="flex-1">
                      {packaging.message} 이미지 {count(packaging.total)}장 · 준비되면 자동으로 내려받습니다.
                    </span>
                    {packaging.progress != null && (
                      <span className="shrink-0 font-semibold">{packaging.progress}%</span>
                    )}
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded bg-blue-100">
                    <div
                      className={`h-full rounded bg-blue-500 ${packaging.progress == null ? 'animate-pulse' : ''}`}
                      style={{ width: packaging.progress != null ? `${packaging.progress}%` : '15%' }}
                    />
                  </div>
                </div>
              )}

              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                  {count(selected.visible_size)}장 (전체의 {percent(selected.share)})
                </span>
                {similarity != null && (
                  <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                    클러스터 내 유사도 {percent(similarity)}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                  이상치 {count(selected.outlier_count)}장
                </span>
                {selected.boundary_ratio != null && (
                  <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                    경계 {(selected.boundary_ratio * 100).toFixed(0)}%
                  </span>
                )}
                <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                  경로 {selected.node_path}
                </span>
              </div>

              {selected.stop_reason && (
                <p className="mt-2 text-xs text-gray-500">더 나누지 않은 이유: {selected.stop_reason}</p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {([['images', `이미지 (${count(total)})`], ['stats', '통계 정보'],
                   ['representatives', '대표 이미지'], ['boundary', '경계 표본'],
                   ['split', '추가 분할 후보']] as [DetailTab, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => key === 'boundary' ? void showBoundary()
                      : key === 'split' ? void showSplit() : setDetailTab(key)}
                    disabled={key === 'split' && !selected.is_leaf}
                    className={`px-3 py-1 text-sm rounded disabled:text-gray-300 disabled:border-gray-200 ${
                      detailTab === key ? 'bg-gray-800 text-white' : 'border border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >{label}</button>
                ))}
              </div>
            </div>

            {/* 남은 공간을 채우고 내부에서만 스크롤 → 클러스터를 바꿔도 패널 높이가 고정된다 */}
            <div className="relative flex-1 min-h-0 overflow-y-auto p-4">
              {loadingItems && (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-16 text-sm text-gray-400">
                  이미지 조회 중...
                </div>
              )}
              <div className={loadingItems ? 'pointer-events-none opacity-40' : ''}>
                {detailTab === 'images' ? (
                  items.length > 0 ? (
                    <>
                      <div className={THUMB_GRID}>
                        {items.map(item => (
                          <ImageResultCard key={item.id} item={item} onOpen={onOpenPreview} compact />
                        ))}
                      </div>
                      {items.length < total && (
                        <div className="mt-3 text-center">
                          <button
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-60"
                          >
                            {loadingMore ? '불러오는 중...' : `더 보기 (${count(items.length)} / ${count(total)})`}
                          </button>
                        </div>
                      )}
                    </>
                  ) : <div className="text-sm text-gray-400">이미지가 없습니다.</div>
                ) : detailTab === 'stats' ? (
                  <div className="space-y-4">
                    <p className="text-[11px] text-gray-400">
                      현재 불러온 {count(items.length)}장 기준입니다 (클러스터 전체 {count(total)}장).
                    </p>
                    {([['카메라 위치', stats.subType], ['수집 방식', stats.uploadMethod],
                       ['촬영 시각', stats.hour]] as [string, [string, number][]][]).map(([title, rows]) => (
                      <div key={title}>
                        <div className="mb-2 text-xs font-semibold text-gray-700">{title}</div>
                        {rows.length === 0 ? (
                          <div className="text-xs text-gray-400">데이터 없음</div>
                        ) : (
                          <div className="space-y-1">
                            {rows.map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2 text-xs">
                                <span className="w-24 shrink-0 truncate text-gray-600">{key}</span>
                                <span className="h-2 flex-1 overflow-hidden rounded bg-gray-200">
                                  <span
                                    className="block h-full rounded bg-gray-700"
                                    style={{ width: `${(value / Math.max(1, items.length)) * 100}%` }}
                                  />
                                </span>
                                <span className="w-12 shrink-0 text-right text-gray-500">{count(value)}장</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : detailTab === 'representatives' ? (
                  <div>
                    <p className="mb-3 text-xs text-gray-500">
                      중심에 가장 가까운 이미지입니다. 이 클러스터가 무엇인지 판단하고 이름을 정할 때 봅니다.
                    </p>
                    <div className={THUMB_GRID}>
                      {selected.representative_thumbnail_urls.map((url, i) => (
                        <button
                          key={`${url}-${i}`}
                          type="button"
                          onClick={() => onOpenPreview({ id: selected.representative_file_ids[i] })}
                          disabled={selected.representative_file_ids[i] == null}
                          className="relative overflow-hidden rounded border border-gray-200 transition hover:border-gray-400"
                        >
                          <img
                            src={embeddingService.mediaUrl(url)}
                            alt=""
                            className="aspect-[4/3] w-full object-cover"
                            loading="lazy"
                          />
                          {selected.representative_similarities?.[i] != null && (
                            <span
                              className="absolute right-1 top-1 rounded bg-gray-900/70 px-1 text-[10px] font-semibold text-white"
                              title={`중심 유사도 ${percent(selected.representative_similarities[i])}`}
                            >{percent(selected.representative_similarities[i])}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : detailTab === 'boundary' ? (
                  <div>
                    <p className="mb-3 text-xs text-gray-500">
                      {boundary?.note
                        ?? '중심에서 가장 먼 사진입니다. 같은 클러스터에 남아 있는 사진끼리 하나의 장면을 이루면 나뉘어야 할 클러스터가 섞인 것입니다.'}
                    </p>
                    <div className={THUMB_GRID}>
                      {(boundary?.samples ?? []).map(s => (
                        <button
                          key={s.file_id}
                          type="button"
                          onClick={() => onOpenPreview({ id: s.file_id })}
                          className="text-center"
                        >
                          <div className="relative">
                            <img
                              src={embeddingService.mediaUrl(s.thumbnail_url)} alt=""
                              className={`aspect-[4/3] w-full rounded object-cover ring-2 transition hover:brightness-90 ${
                                s.same_cluster ? 'ring-amber-400' : 'ring-gray-200'
                              }`}
                              loading="lazy"
                            />
                            {s.distance_to_centroid != null && (
                              <span
                                className="absolute right-1 top-1 rounded bg-gray-900/70 px-1 text-[10px] font-semibold text-white"
                                title={`중심 유사도 ${percent(1 - s.distance_to_centroid)} · 중심거리 ${s.distance_to_centroid.toFixed(3)}`}
                              >{percent(1 - s.distance_to_centroid)}</span>
                            )}
                          </div>
                          <div className="mt-1 truncate text-[10px] text-gray-400">
                            {s.same_cluster ? '같은 클러스터' : `→ ${s.cluster_path}`}
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="mt-3 text-[11px] text-gray-400">
                      노란 테두리 = 이 클러스터에 그대로 남아 있는 사진 · 회색 = 다른 최종 클러스터로 간 사진
                    </p>
                  </div>
                ) : (
                  <div>
                    {split?.note && <p className="mb-3 text-xs text-gray-500">{split.note}</p>}
                    {(split?.candidates ?? []).map(c => (
                      <div key={c.index} className="mb-3 border border-gray-200 rounded p-3">
                        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                          <span className="font-semibold text-gray-800">후보 {c.index}</span>
                          <span className="text-gray-600">{count(c.size)}장</span>
                          <span className={c.tighter_than_parent ? 'text-green-700' : 'text-gray-400'}>
                            응집도 {c.cohesion.toFixed(3)}
                            {c.tighter_than_parent ? ' (원래 클러스터보다 조밀)' : ' (원래 클러스터보다 느슨)'}
                          </span>
                        </div>
                        <div className={THUMB_GRID}>
                          {c.representative_thumbnail_urls.map((url, i) => (
                            <button
                              key={`${url}-${i}`}
                              type="button"
                              onClick={() => onOpenPreview({ id: c.representative_file_ids[i] })}
                              disabled={c.representative_file_ids[i] == null}
                              className="overflow-hidden rounded border border-gray-200 transition hover:border-gray-400"
                            >
                              <img
                                src={embeddingService.mediaUrl(url)} alt=""
                                className="aspect-[4/3] w-full object-cover" loading="lazy"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {split && split.not_split_reasons.length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs text-gray-600">
                        <div className="mb-1 font-semibold">지금 더 나누지 않은 이유</div>
                        <ul className="list-inside list-disc space-y-0.5">
                          {split.not_split_reasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                    {split && (
                      <p className="mt-2 text-[11px] text-gray-400">
                        경계 표본 {count(split.analyzed)}장을 분석했고, {count(split.min_candidate_size)}장 이상
                        뭉친 덩어리만 후보로 봅니다. 후보가 있다고 자동으로 나누지는 않습니다.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {selected && (
        <ClusterDownloadModal
          isOpen={downloadOpen}
          defaultName={selected.label || `cluster_${selected.node_path}`}
          fileCount={selected.visible_size}
          onClose={() => setDownloadOpen(false)}
          onSubmit={startDownload}
        />
      )}
    </div>
  )
}
